import React, { useEffect, useState, useCallback } from "react";

import ThreeColumnLayout from "@/components/common/three-column-layout";
import { ConfirmationModalBase } from "@/components/common/modals";
import type { Node } from "@xyflow/react";
import FullModal from "../../full-modal";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { Button, Icon } from "@elaniintc/goes-design-system";
import { useUpdateNode } from "@/workflow_api/services/nodes.api";
import type { UpdateNodeBody } from "@/workflow_api/services/nodes.api";
import { useNodeNameValidation } from "@/workflow_api/hooks/useNodeNameValidation";
import type { NodeData } from "@/workflow_api/types/workflow";
import { DropdownList } from "@/components/ui/dropdown-list/dropdown-list";
import Variables from "@/workflow_api/components/variables";
import Empty from "@/workflow_api/components/variables/empty";

import { NodeTypes } from "@/workflow_api/types/workflow";
import { mapNodeTypeToApi, mapRefNameToNodeType } from "./node-type-mapping";

import { ConnectionManager } from "./connection-manager";
import { CommonAncestorValidator } from "./common-ancestor-validator";
import type { MergeEntry, MergeNodeConfig } from "./types";
import { MIN_ENTRIES, MAX_ENTRIES } from "./types";
import { mergeNodeConfigSchema } from "./validation";
import { mergeNodeToApiFormat } from "./serialization/index";
import { serializeMergeEdges } from "./serialization/edges";
import { MergeEntryField } from "./merge-entry-field";
import { NodeTypeSelector } from "./node-type-selector-component";

type ExtendedNodeData = NodeData & {
  ref_name?: string;
  inputs?: Array<{
    type: string;
    calculations?: Array<{
      operation: string;
      inputs?: Array<{ type: string }>;
    }>;
  }>;
  calculations?: Array<{
    operation: string;
    inputs?: Array<{ type: string }>;
  }>;
};

interface MergeDetailsProps {
  initialTitle?: string;
}

export const MergeDetails: React.FC<MergeDetailsProps> = ({
  initialTitle = "Merge",
}) => {
  const { edges } = useWorkflowStore();
  const nodeTypes = useCallback((node: string) => {
    switch (node) {
      case NodeTypes.FORM:
        return [
          "action",
          "bars",
          "bg-surface-action-accent-quiet border-action-quiet text-action-quiet",
          "Formulario",
        ] as const;
      case NodeTypes.IF:
        return [
          "neutral",
          "toggle-on",
          "bg-neutral-object-accent-silent border-neutral text-body",
          "Operador IF",
        ] as const;
      case NodeTypes.SWITCH:
        return [
          "neutral",
          "arrows-cross",
          "bg-neutral-object-accent-silent border-neutral text-body",
          "Operador Switch",
        ] as const;
      case NodeTypes.MULTITHREAD:
        return [
          "neutral",
          "code-branch",
          "bg-neutral-object-accent-silent border-neutral text-body",
          "Multihilos",
        ] as const;
      case NodeTypes.WAIT:
        return [
          "neutral",
          "hourglass-half",
          "bg-neutral-object-accent-silent border-neutral text-body",
          "Wait",
        ] as const;
      case NodeTypes.MERGE:
        return [
          "neutral",
          "code-merge",
          "bg-neutral-object-accent-silent border-neutral text-body",
          "Merge",
        ] as const;
      case NodeTypes.CALCULATED:
        return [
          "success",
          "function",
          "bg-surface-sucess-accent-quiet border-contextual-success text-success",
          "Campo Calculado",
        ] as const;
      case NodeTypes.ASSIGN_GROUP:
        return [
          "info",
          "users",
          "bg-surface-info-accent-quiet border-contextual-info text-info",
          "Asignar Grupo",
        ] as const;
      case NodeTypes.DATA_CENTER:
        return [
          "info",
          "table",
          "bg-surface-info-accent-quiet border-contextual-info-quiet text-info-quiet",
          "Centro de Datos",
        ] as const;
      case NodeTypes.EXPEDIENTE:
        return [
          "info",
          "folder-open",
          "bg-surface-info-accent-quiet border-contextual-info text-info",
          "Expediente",
        ] as const;
      case NodeTypes.NOTIFICATION:
        return [
          "info",
          "bells",
          "bg-surface-info-accent-quiet border-contextual-info text-info",
          "Notificación",
        ] as const;
      case NodeTypes.CORRECTION:
        return [
          "action",
          "file-edit",
          "bg-surface-action-accent-quiet border-action-quiet text-action-quiet",
          "Revisión",
        ] as const;
      case NodeTypes.DOC_GENERATOR:
        return [
          "action",
          "file-lines",
          "bg-surface-action-accent-quiet border-contextual-action-quiet text-action-quiet",
          "Generador de Documentos",
        ] as const;
      case NodeTypes.SIGN_DOCUMENT:
        return [
          "action",
          "signature",
          "bg-surface-action-accent-quiet border-contextual-action-quiet text-action-quiet",
          "Firma Digital",
        ] as const;
      case NodeTypes.RESOLUTION:
        return [
          "action",
          "files",
          "bg-surface-action-accent-quiet border-contextual-action-quiet text-action-quiet",
          "Resolución de trámites",
        ] as const;
      case NodeTypes.PAYMENT:
        return [
          "warning",
          "dollar-sign",
          "bg-surface-warning-accent text-warning-quiet border-contextual-warning-quiet",
          "Pasarela de Pago",
        ] as const;
      case NodeTypes.N8N:
        return [
          "warning",
          "hexagon-nodes",
          "bg-surface-warning-accent-quiet border-contextual-warning-quiet text-warning-quiet",
          "N8N",
        ] as const;
      case NodeTypes.INTEROPERABILITY:
        return [
          "warning",
          "diagram-nested",
          "bg-surface-warning-accent text-warning-quiet border-contextual-warning-quiet",
          "Interoperabilidad",
        ] as const;
      default:
        return [
          "neutral",
          "bars",
          "bg-surface-action-accent-quiet border-action-quiet text-action-quiet",
          "Desconocido",
        ] as const;
    }
  }, []);

  const getNodeTypeIcon = useCallback(
    (nodeType: string) => {
      const [, iconName, bgBorderColor, textColor] = nodeTypes(nodeType);
      return {
        type: "fas" as const,
        name: iconName,
        bgBorderColor,
        textColor,
      };
    },
    [nodeTypes],
  );

  const {
    nodeDetailsOpen,
    selectedNodeId,
    setNodeDetailsOpen,
    setSelectedNodeId,
  } = useUIStore();

  const { getNodeById, flowId, nodes } = useWorkflowStore();
  const { getNameValidationError } = useNodeNameValidation(selectedNodeId);

  const { mutateAsync: updateNode } = useUpdateNode(flowId);

  const [config, setConfig] = useState<MergeNodeConfig>({
    merge_node_type: "",
    entries: [
      { name: "", nodeId: "", type: "MERGE_ENTRY" },
      { name: "", nodeId: "", type: "MERGE_ENTRY" },
    ],
    manual_continue: false,
  });

  const getStaticNodeOutputSummary = useCallback(
    (node: Node): Record<string, number> | null => {
      const data = (node?.data as unknown as ExtendedNodeData) || {};
      const summary: Record<string, number> = { date: 2 };

      if (data.type === NodeTypes.DOC_GENERATOR) {
        summary.file = 1;
      } else if (data.type === NodeTypes.PAYMENT) {
        summary.boolean = 1;
      } else if (data.type === NodeTypes.SIGN_DOCUMENT) {
        const generatedDocs = data.inputs?.find(
          (i) => (i as Record<string, unknown>).name === "generated_documents",
        );
        const docCount =
          (
            (generatedDocs as Record<string, unknown>)?.documents as Array<{
              inputs?: unknown[];
            }>
          )?.reduce((count, doc) => count + (doc.inputs?.length || 0), 0) || 1;
        summary.file = docCount;
      } else if (
        data.type === NodeTypes.CALCULATED ||
        data.ref_name === "calculated-field" ||
        data.type === "calculated-node" ||
        data.ref_name === "calculated-node"
      ) {
        const inputs = data.inputs || [];
        const calculations =
          inputs[0]?.calculations ||
          data.calculations ||
          inputs.find((i) => i.type === "CALCULATED")?.calculations;
        const calculation = Array.isArray(calculations)
          ? calculations[0]
          : null;

        if (calculation) {
          const operationOutputMap: Record<string, string> = {
            DATE_TO_STRING: "string",
            CONCAT: "string",
            UPPERCASE: "string",
            LOWERCASE: "string",
            TRIM: "string",
            SUBSTRING: "string",
            REPLACE: "string",
            DATE_DIFF: "number",
            SUM: "number",
            SUBTRACT: "number",
            MULTIPLY: "number",
            DIVIDE: "number",
            MODULO: "number",
            ROUND: "number",
            CEIL: "number",
            FLOOR: "number",
            ABS: "number",
            POWER: "number",
            SQRT: "number",
            DATE_ADD: "date",
            DATE_SUBTRACT: "date",
            STRING_TO_DATE: "date",
            NOW: "date",
            EQUALS: "boolean",
            NOT_EQUALS: "boolean",
            GREATER_THAN: "boolean",
            LESS_THAN: "boolean",
            GREATER_THAN_OR_EQUAL: "boolean",
            LESS_THAN_OR_EQUAL: "boolean",
            AND: "boolean",
            OR: "boolean",
            NOT: "boolean",
          };
          const outputType =
            operationOutputMap[calculation.operation] || "string";
          summary[outputType] = 1;
        }
      } else if (data.type === NodeTypes.DATA_CENTER) {
        const operationInput = data.inputs?.find(
          (i) => (i as Record<string, unknown>).name === "action",
        );
        const action = (operationInput?.value as string) || "";
        if (action === "read") {
          summary.array = 1;
        } else if (action) {
          summary.array = 1;
          summary.number = 1;
        }
      } else if (data.type === NodeTypes.EXPEDIENTE) {
        summary.string = 2;
      } else {
        // For node types whose outputs require async resolution
        // (FORM, INTEROPERABILITY, N8N, etc.), we cannot determine
        // the output structure synchronously. Return null to skip
        // the summary comparison — the node type filter already
        // ensures only same-type nodes are shown.
        return null;
      }

      return summary;
    },
    [],
  );

  const getNodeOutputSummary = useCallback(
    (nodeId: string) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (node) {
        return getStaticNodeOutputSummary(node);
      }
      return null;
    },
    [nodes, getStaticNodeOutputSummary],
  );

  const areSummariesEqual = (
    s1: Record<string, number>,
    s2: Record<string, number>,
  ) => {
    const keys1 = Object.keys(s1).sort();
    const keys2 = Object.keys(s2).sort();
    if (keys1.length !== keys2.length) return false;
    return keys1.every((key) => s1[key] === s2[key]);
  };

  const selectedNode = React.useMemo(
    () => getNodeById(selectedNodeId || ""),
    [getNodeById, selectedNodeId],
  );
  const nodeData = selectedNode?.data as unknown as NodeData | undefined;

  const [title, setTitle] = useState(nodeData?.displayName || initialTitle);
  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );
  const [showNodeTitleValidation, setShowNodeTitleValidation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedConnectedNodeId, setSelectedConnectedNodeId] = useState("");
  const [sourceNodeSearchTerm, setSourceNodeSearchTerm] = useState("");
  const [manualConfirmation, setManualConfirmation] = useState(() => {
    return nodeData?.manual_continue ?? false;
  });

  const handleManualConfirmationChange = useCallback((checked: boolean) => {
    setManualConfirmation(checked);
    setConfig((prev) => ({ ...prev, manual_continue: checked }));
  }, []);

  const getAvailableNodesForEntry = (entryIndex: number) => {
    const selectedNodeIdsInOtherEntries = config.entries
      .filter((_: MergeEntry, i: number) => i !== entryIndex)
      .map((e: MergeEntry) => e.nodeId)
      .filter(Boolean);

    let availableNodes = nodes.filter(
      (node) =>
        node.id !== selectedNodeId &&
        !selectedNodeIdsInOtherEntries.includes(node.id),
    );

    if (selectedConnectedNodeId) {
      availableNodes = availableNodes.filter((node) =>
        CommonAncestorValidator.isDownstream(
          selectedConnectedNodeId,
          node.id,
          edges,
        ),
      );
    }

    if (config.merge_node_type) {
      availableNodes = availableNodes.filter((node) => {
        const nodeData = node.data as unknown as ExtendedNodeData;
        const nodeType = nodeData?.ref_name || nodeData?.type || "";
        return nodeType === config.merge_node_type;
      });
    }

    const firstEntryNodeId = config.entries[0]?.nodeId;
    if (entryIndex > 0 && firstEntryNodeId) {
      const referenceSummary = getNodeOutputSummary(firstEntryNodeId);
      if (referenceSummary) {
        availableNodes = availableNodes.filter((node) => {
          const nodeSummary = getNodeOutputSummary(node.id);
          if (!nodeSummary) return true;
          return areSummariesEqual(referenceSummary, nodeSummary);
        });
      }
    }

    availableNodes = availableNodes.filter((node) => {
      const allOutgoingEdges = edges.filter((edge) => edge.source === node.id);
      const hasOutgoingConnection = allOutgoingEdges.some(
        (edge) => edge.target !== selectedNodeId,
      );

      const nonErrorEdges = allOutgoingEdges.filter(
        (edge) =>
          !(
            edge.sourceHandle === "failure" ||
            edge.sourceHandle === "error" ||
            edge.sourceHandle === "timer_expired" ||
            edge.sourceHandle === "timeout" ||
            edge.sourceHandle === "exception" ||
            edge.sourceHandle === "rejected" ||
            edge.sourceHandle === "catch"
          ),
      );

      return !hasOutgoingConnection || nonErrorEdges.length === 0;
    });

    const options = availableNodes.map((node) => {
      const nodeData = node.data as unknown as ExtendedNodeData;
      const nodeName = nodeData?.displayName || `Nodo ${node.id.slice(0, 8)}`;
      const nodeType = nodeData?.ref_name || nodeData?.type || "";
      const iconInfo = getNodeTypeIcon(nodeType);

      return {
        value: node.id,
        label: nodeName,
        iconConfig: {
          type: "fas" as const,
          name: iconInfo.name,
        },
        iconPosition: "left" as const,
      };
    });

    return options;
  };

  const connectedNodesOptions = React.useMemo(() => {
    if (!selectedNodeId) return [];

    const allNodes = nodes.filter((node) => node.id !== selectedNodeId);

    const allowedNodeTypes = [NodeTypes.IF, NodeTypes.SWITCH];
    const filteredNodes = allNodes.filter((node) => {
      const nodeData = node.data as unknown as ExtendedNodeData;
      const nodeType = nodeData?.ref_name || nodeData?.type || "";
      return allowedNodeTypes.includes(
        nodeType as typeof NodeTypes.IF | typeof NodeTypes.SWITCH,
      );
    });

    return filteredNodes.map((node) => {
      const nodeData = node.data as unknown as ExtendedNodeData;
      const nodeName = nodeData?.displayName || `Nodo ${node.id.slice(0, 8)}`;
      const nodeType = nodeData?.ref_name || nodeData?.type || "";

      return {
        value: node.id,
        label: nodeName,
        iconConfig: {
          type: "fas" as const,
          name: getNodeTypeIcon(nodeType).name,
        },
        iconPosition: "left" as const,
      };
    });
  }, [nodes, selectedNodeId, getNodeTypeIcon]);

  useEffect(() => {
    if (nodeData?.inputs && Array.isArray(nodeData.inputs)) {
      const mergeTypeInput = nodeData.inputs?.find(
        (input: { name: string; type: string }) =>
          input.name === "merge_node_type" && input.type === "STRING",
      );

      const entryInputs = nodeData.inputs
        ?.filter(
          (input: { name: string; type: string }) =>
            input.name.startsWith("entry_") && input.type === "MERGE_ENTRY",
        )
        .sort((a: { name: string }, b: { name: string }) => {
          const aNum = parseInt(a.name.replace("entry_", ""));
          const bNum = parseInt(b.name.replace("entry_", ""));
          return aNum - bNum;
        });

      if (mergeTypeInput || entryInputs.length > 0) {
        try {
          const loadedConfig: MergeNodeConfig = {
            merge_node_type: mapRefNameToNodeType(
              (mergeTypeInput?.value as string) || "",
            ),
            entries: entryInputs.map((entry: { value: unknown }) => {
              let parsedValue = entry.value || "";

              if (typeof entry.value === "string") {
                try {
                  parsedValue = JSON.parse(entry.value);
                } catch (parseError) {
                  console.error("Error parsing merge entry value:", parseError);
                  parsedValue = { name: "", node_id: "" };
                }
              }

              const valueObj =
                typeof parsedValue === "object" && parsedValue !== null
                  ? (parsedValue as { name?: string; node_id?: string })
                  : { name: "", node_id: "" };

              return {
                name: valueObj?.name || "",
                nodeId: valueObj?.node_id || "",
                type: "MERGE_ENTRY",
              };
            }),
            manual_continue: Boolean(nodeData?.manual_continue || false),
          };

          if (loadedConfig.entries.length > 0) {
            const firstEntryNodeId = loadedConfig.entries.find(
              (e) => e.nodeId,
            )?.nodeId;
            if (firstEntryNodeId) {
              const firstEntryNode = nodes.find(
                (n) => n.id === firstEntryNodeId,
              );
              if (firstEntryNode) {
                const entryUiType = firstEntryNode.data.type;
                const loadedUiType = loadedConfig.merge_node_type;

                if (
                  mapNodeTypeToApi(entryUiType as string) ===
                  mapNodeTypeToApi(loadedUiType as string)
                ) {
                  loadedConfig.merge_node_type = entryUiType as string;
                }
              }
            }
          }

          setConfig(loadedConfig);
          setManualConfirmation(loadedConfig.manual_continue);

          const validEntries = loadedConfig.entries.filter((e) => e.nodeId);

          if (validEntries.length >= 2) {
            try {
              const validation = CommonAncestorValidator.validateCommonAncestor(
                validEntries,
                nodes,
                edges,
              );
              if (validation.isValid && validation.commonAncestor) {
                setSelectedConnectedNodeId(validation.commonAncestor);
              } else {
                const firstEntryNode = nodes.find(
                  (n) => n.id === validEntries[0].nodeId,
                );
                if (firstEntryNode) {
                  const incomingEdge = edges.find(
                    (edge) => edge.target === firstEntryNode.id,
                  );
                  if (incomingEdge)
                    setSelectedConnectedNodeId(incomingEdge.source);
                }
              }
            } catch (e) {
              console.error("Error calculating common ancestor on load:", e);
            }
          } else if (validEntries.length === 1 && validEntries[0].nodeId) {
            const firstEntryNode = nodes.find(
              (n) => n.id === validEntries[0].nodeId,
            );
            if (firstEntryNode) {
              const incomingEdge = edges.find(
                (edge) => edge.target === firstEntryNode.id,
              );
              if (incomingEdge) {
                setSelectedConnectedNodeId(incomingEdge.source);
              }
            }
          }
        } catch (error) {
          console.error("Error parsing merge config:", error);
          setConfig((prev) => ({
            ...prev,
            merge_node_type: prev.merge_node_type || "",
            entries:
              prev.entries.length > 0
                ? prev.entries
                : [
                    { name: "", nodeId: "", type: "MERGE_ENTRY" },
                    { name: "", nodeId: "", type: "MERGE_ENTRY" },
                  ],
          }));
        }
      }
    }
  }, [nodeData?.inputs, nodeData?.manual_continue, edges, nodes]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const configToSave = {
        ...config,
        manual_continue: manualConfirmation,
        display_name: title,
      };

      mergeNodeConfigSchema.parse(configToSave);

      if (configToSave.entries.length >= 2) {
        const validation = CommonAncestorValidator.validateCommonAncestor(
          configToSave.entries,
          nodes,
          edges,
        );

        if (!validation.isValid) {
          return;
        }
      }

      const apiNode = mergeNodeToApiFormat(
        configToSave,
        flowId,
        (selectedNode?.data?.order as number) || 0,
        {
          x: (selectedNode?.position?.x as number) || 0,
          y: (selectedNode?.position?.y as number) || 0,
        },
      );

      const mergeEdges = serializeMergeEdges(configToSave.entries);
      if (mergeEdges.length > 0) {
        apiNode.edges = mergeEdges;
      }

      await updateNode({
        node_id: selectedNodeId || "",
        node: apiNode as UpdateNodeBody,
      });

      if (selectedNodeId) {
        ConnectionManager.manageOutputVisibility(
          selectedNodeId,
          configToSave.entries,
        );
      }

      setNodeDetailsOpen(false);
    } catch (error) {
      console.error("Error saving merge node:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const isValid =
    config.merge_node_type &&
    config.entries.length >= MIN_ENTRIES &&
    config.entries.every((entry, index) => {
      if (!entry.name || !entry.nodeId) return false;
      if (index > 0 && config.entries[0]?.nodeId) {
        const referenceSummary = getNodeOutputSummary(config.entries[0].nodeId);
        const currentSummary = getNodeOutputSummary(entry.nodeId);
        if (
          referenceSummary &&
          currentSummary &&
          !areSummariesEqual(referenceSummary, currentSummary)
        ) {
          return false;
        }
      }
      return true;
    });

  const addEntry = () => {
    if (config.entries.length >= MAX_ENTRIES) return;

    const newEntry: MergeEntry = {
      name: "",
      nodeId: "",
      type: "MERGE_ENTRY",
    };

    setConfig((prev) => ({
      ...prev,
      entries: [...prev.entries, newEntry],
    }));
  };

  const removeEntry = (index: number) => {
    if (index < MIN_ENTRIES) return;

    setConfig((prev) => ({
      ...prev,
      entries: prev.entries.filter((_, i) => i !== index),
    }));
  };

  const updateEntryName = (index: number, name: string) => {
    setConfig((prev) => {
      const newEntries = [...prev.entries];
      if (!newEntries[index]) {
        newEntries[index] = { name: "", nodeId: "", type: "MERGE_ENTRY" };
      } else {
        newEntries[index] = { ...newEntries[index] };
      }

      newEntries[index].name = name;

      return { ...prev, entries: newEntries };
    });
  };

  const updateEntryNodeId = (index: number, newNodeId: string) => {
    setConfig((prev) => {
      const newEntries = [...prev.entries];
      if (!newEntries[index]) {
        newEntries[index] = { name: "", nodeId: "", type: "MERGE_ENTRY" };
      } else {
        newEntries[index] = { ...newEntries[index] };
      }
      newEntries[index].nodeId = newNodeId;
      return { ...prev, entries: newEntries };
    });
  };

  const leftContent = (
    <div className="bg-neutral-foreground overflow-auto divide-y divide-(--border-neutral-quiet)">
      {selectedNodeId ? <Variables sourceNodeId={selectedNodeId} /> : <Empty />}
    </div>
  );

  const centerContent = (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-1 w-full">
        <label className="font-semibold text-body-loud text-sm leading-2xl">
          Selecciona el nodo a fusionar
        </label>
        <DropdownList
          options={connectedNodesOptions
            .filter((opt) =>
              opt.label
                .toLowerCase()
                .includes(sourceNodeSearchTerm.toLowerCase()),
            )
            .map((opt) => ({
              value: opt.value,
              label: opt.label,
              icon: opt.iconConfig,
            }))}
          selectedValue={(() => {
            const opt = connectedNodesOptions.find(
              (opt) => opt.value === selectedConnectedNodeId,
            );
            return opt
              ? { value: opt.value, label: opt.label, icon: opt.iconConfig }
              : undefined;
          })()}
          placeholder="Selecciona un nodo"
          widthClassName="w-full"
          search={{
            placeholder: "Buscar nodo...",
            query: sourceNodeSearchTerm,
            onSearch: setSourceNodeSearchTerm,
          }}
          toggleSelection={(item) => setSelectedConnectedNodeId(item.value)}
        />
      </div>

      <div>
        <NodeTypeSelector
          label="Selección de tipo de nodo a fusionar"
          required
          value={config.merge_node_type}
          onChange={(value: string) =>
            setConfig((prev) => ({ ...prev, merge_node_type: value }))
          }
          disabled={!selectedConnectedNodeId && !config.entries[0]?.nodeId}
        />
      </div>

      <div className="border-t border-neutral-object-accent"></div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-body-loud">
          Entradas de fusión
        </h3>

        {config.entries.map((entry, index) => (
          <MergeEntryField
            key={index}
            index={index}
            entry={entry}
            isRequired={index < MIN_ENTRIES}
            canDelete={index >= MIN_ENTRIES}
            disabled={
              !config.merge_node_type ||
              (index > 0 && !config.entries[0]?.nodeId)
            }
            error={(() => {
              if (index === 0 || !config.entries[0]?.nodeId || !entry.nodeId)
                return undefined;
              const referenceSummary = getNodeOutputSummary(
                config.entries[0].nodeId,
              );
              const currentSummary = getNodeOutputSummary(entry.nodeId);
              if (
                referenceSummary &&
                currentSummary &&
                !areSummariesEqual(referenceSummary, currentSummary)
              ) {
                return "La estructura de outputs no coincide con la primera entrada";
              }
              return undefined;
            })()}
            onNameChange={updateEntryName}
            onNodeChange={updateEntryNodeId}
            onDelete={removeEntry}
            getNodeOptions={getAvailableNodesForEntry}
            getLabelForValue={(nodeId) => {
              const node = nodes.find((n) => n.id === nodeId);
              return (
                (node?.data as unknown as ExtendedNodeData)?.displayName ||
                nodeId
              );
            }}
          />
        ))}

        {config.entries.length < MAX_ENTRIES && (
          <Button
            variant="tertiary"
            size="xs"
            onClick={addEntry}
            className="w-fit"
            disabled={!config.merge_node_type || !config.entries[0]?.nodeId}
          >
            <Icon type="far" name="plus" />
            <span>Añadir entrada</span>
          </Button>
        )}
      </div>
    </div>
  );

  const rightContent = (
    <div className="bg-neutral-foreground overflow-auto divide-y divide-(--border-neutral-quiet) h-full">
      <Empty />
    </div>
  );

  return (
    <>
      <FullModal
        isOpen={nodeDetailsOpen}
        onClose={() => {
          setModalState("close-warning");
        }}
        onSave={handleSave}
        isSaveValid={Boolean(isValid)}
        isLoading={isSaving}
      >
        <ThreeColumnLayout
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
            hideSearch: true,
          }}
          centerHeader={{
            icon: "code-merge",
            title: title || "Merge",
            color: "neutral",
            editable: true,
            onTitleChange: (newTitle: string) => {
              setTitle(newTitle);
              if (newTitle.trim() === "") {
                setShowNodeTitleValidation(true);
              }
            },
            onTitleBlur: () => setShowNodeTitleValidation(true),
            validationError: getNameValidationError(title),
            showValidation: showNodeTitleValidation,
            manualConfirmation,
            onManualConfirmationChange: handleManualConfirmationChange,
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Outputs",
            color: "neutral",
          }}
          leftContent={leftContent}
          centerContent={centerContent}
          rightContent={rightContent}
        />
      </FullModal>

      {modalState === "close-warning" && (
        <ConfirmationModalBase
          isOpen={modalState === "close-warning"}
          onClose={() => setModalState("idle")}
          onAccept={() => {
            setNodeDetailsOpen(false);
            setSelectedNodeId("");
            setModalState("idle");
          }}
          title="¿Cerrar sin guardar?"
          description="Tienes cambios sin guardar. ¿Estás seguro de que quieres cerrar sin guardar los cambios?"
          acceptText="Cerrar sin guardar"
          cancelText="Continuar editando"
        />
      )}
    </>
  );
};

export default MergeDetails;
